import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { fetchBulkVehicles, fetchBulkVehiclesMetadata } from './bulkEditIndex.thunks';
import { initialState as baseInitialState, WS_ALLOWED_FIELDS } from './constants';
import { getBulkEditUpdateErrorMessage, mapSanitizedChanges } from './bulkEditIndex.helpers';

export const bulkVehiclesAdapter = createEntityAdapter();

export const initialState = {
	...baseInitialState,
	data: bulkVehiclesAdapter.getInitialState(),
};

export const bulkEditIndexSlice = createSlice({
	name: 'bulkEditIndex',
	initialState,
	reducers: {
		applyVehicleWsPatch(state, { payload }) {
			const { vehicleId, changes = {}, update_result: updateResults, status: statusFromPayload } = payload || {};

			if (!vehicleId) return;

			const status = statusFromPayload ?? changes?.status;
			const statusChanges = status !== undefined ? { status } : null;

			const sanitizedChanges = WS_ALLOWED_FIELDS.reduce((acc, field) => {
				if (changes[field] !== undefined) {
					acc[field] = changes[field];
				}

				return acc;
			}, {});

			const mappedChanges = mapSanitizedChanges(sanitizedChanges);

			// External updates (no update_result) should still patch state but not touch submission flags/errors
			if (!updateResults) {
				const vehicleChanges = {
					...(mappedChanges || {}),
					...(statusChanges || {}),
				};

				if (Object.keys(vehicleChanges).length) {
					bulkVehiclesAdapter.updateOne(state.data, { id: vehicleId, changes: vehicleChanges });
				}

				return;
			}

			const shouldApplyChanges = updateResults?.success || false;
			const errorMessage = getBulkEditUpdateErrorMessage(updateResults);

			// Flag - has a response been received for this vehicle
			state.updateResponseReceivedByVehicle[vehicleId] = true;

			if (errorMessage) {
				state.errors.updateErrorsByVehicle[vehicleId] = errorMessage;
			} else {
				delete state.errors.updateErrorsByVehicle[vehicleId];
			}

			const vehicleChanges = {
				...(shouldApplyChanges ? mappedChanges : {}),
				...(statusChanges || {}),
			};

			if (Object.keys(vehicleChanges).length) {
				bulkVehiclesAdapter.updateOne(state.data, { id: vehicleId, changes: vehicleChanges });
			}
		},
		setFilters(state, action) {
			state.query = {
				...state.query,
				filters: { ...state.query.filters, ...action.payload },
				page: 1,
			};
		},
		setSort(state, action) {
			const { direction, ...flags } = action.payload || {};
			const flagKey = Object.keys(flags || {}).find((key) => flags[key] !== undefined);

			const resetSort = {
				order_by_most_recent_activity: false,
				order_by_floor_price: false,
				order_by_starting_bid_amount: false,
				direction: null,
			};

			if (!flagKey) {
				state.query = { ...state.query, sort: resetSort, page: 1 };
				return;
			}

			const flagValue = Boolean(flags[flagKey]);

			state.query = {
				...state.query,
				sort: {
					...resetSort,
					[flagKey]: flagValue,
					direction: flagValue ? direction || null : null,
				},
				page: 1,
			};
		},
		setPage(state, action) {
			state.query = { ...state.query, page: action.payload };
		},
		clearErrors(state) {
			state.errors.dataErrorMessage = '';
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchBulkVehicles.pending, (state) => {
				state.loading = true;
				state.success = undefined;
				state.errors.dataErrorMessage = '';
			})
			.addCase(fetchBulkVehicles.fulfilled, (state, { payload }) => {
				const { bulkVehicles = [], metadata } = payload || {};

				bulkVehiclesAdapter.setAll(state.data, bulkVehicles);

				state.metadata = metadata || state.metadata;
				state.loading = false;
				state.success = true;
			})
			.addCase(fetchBulkVehicles.rejected, (state, { payload }) => {
				state.loading = false;
				state.success = false;
				state.errors.dataErrorMessage = payload || 'Failed to fetch bulk vehicles';
			})
			.addCase(fetchBulkVehiclesMetadata.fulfilled, (state, { payload }) => {
				state.metadata = payload?.metadata || state.metadata;
			})
			.addCase(HYDRATE, (state, { payload }) => {
				const payloadData = payload?.bulkEditIndex;

				const payloadVehicles = payloadData?.bulkVehicles || payloadData?.data;

				if (payloadVehicles) {
					const vehiclesArray =
						Array.isArray(payloadVehicles) ? payloadVehicles : (
							payloadVehicles?.ids?.map((id) => payloadVehicles.entities?.[id]).filter(Boolean)
						);

					bulkVehiclesAdapter.setAll(state.data, vehiclesArray);
				}

				state.metadata = payloadData?.metadata ?? state.metadata;
			});
	},
});

export const { applyVehicleWsPatch, setFilters, setSort, setPage, clearErrors } = bulkEditIndexSlice.actions;

export const {
	selectIds: selectBulkVehicleIds,
	selectById: selectBulkVehicleById,
	selectEntities: selectBulkVehicleEntities,
	selectAll: selectAllBulkVehicles,
} = bulkVehiclesAdapter.getSelectors((state) => state.bulkEditIndex.data);

export default bulkEditIndexSlice.reducer;
